import { computed, type ComputedRef, ref, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import useContactsService from '@/composables/apiServices/useContactsService';
import { ERecipientInputItemTypes } from '@/types/components/composer/enums';
import type {
  IRecipientInputItem,
  IRecipientInputItemContact,
  IRecipientInputItemGroup,
  IRecipientInputItemNonCustom,
  IRecipientInputItemNonGroup,
  IRecipientInputItemRecentParty
} from '@/types/components/composer/RecipientInput';
import type { IContact, IRecentParty } from '@/types/services/contact.service';
import { useQueryClient } from '@tanstack/vue-query';
import { validateEmail } from '@/helpers/urlFormat';

export function useContactsData(selectedEmailsMap?: ComputedRef<Record<string, boolean>>) {
  // The current user input string
  const userInputString = ref<string>('');
  const debouncedUserInputString = ref<string>('');

  watch(
    userInputString,
    useDebounceFn(
      (newVal) => {
        debouncedUserInputString.value = newVal;
      },
      250,
      { maxWait: 1000 }
    ),
    { immediate: true }
  );

  const { getContactsList, getRecentParties, getGroupsList, getRecentPartiesQueryOptions } =
    useContactsService();

  const queryClient = useQueryClient();

  const { data: contactsData, isFetching: isContactsListFetching } = getContactsList();
  const { data: groupsData } = getGroupsList();
  const { data: recentPartiesData, isFetching: isRecentPartiesFetching } =
    getRecentParties(debouncedUserInputString);

  const contactGroupsWithContacts = computed(() => {
    if (!contactsData.value || !groupsData.value?.data) return [];

    const groupMap = new Map<number, string>(
      groupsData.value.data.map((group) => [group.id, group.name])
    );

    const res = contactsData.value.reduce<Record<string, IRecipientInputItemGroup>>(
      (acc, contact) => {
        if (!contact.groups.length || !contact.emails?.length) return acc;

        contact.groups.forEach((groupId) => {
          const groupName = groupMap.get(groupId);
          if (!groupName || !groupName.includes(userInputString.value)) return;

          if (!acc[groupName]) {
            acc[groupName] = {
              id: groupId,
              type: ERecipientInputItemTypes.GROUP,
              name: groupName,
              contacts: [],
              hasAvatar:
                groupsData.value.data.find((item) => item.id === groupId)?.hasAvatar || false
            };
          }

          acc[groupName].contacts.push(contact.emails[0] ?? '');
        });

        return acc;
      },
      {}
    );

    return Object.values(res);
  });

  const filterContactsByInput = (contacts: IContact[], input: string) => {
    if (!input) return contacts;
    return contacts.reduce<IContact[]>((acc, contact) => {
      if (contact.fullName.toLowerCase().includes(input.toLowerCase())) {
        acc.push(...contact.emails.map((email) => ({ ...contact, emails: [email] })));
      } else {
        const matchingEmailIndex = contact.emails.findIndex((email) =>
          email.toLowerCase().includes(input.toLowerCase())
        );
        if (matchingEmailIndex !== -1 && contact.emails[matchingEmailIndex] !== undefined) {
          acc.push({ ...contact, emails: [contact.emails[matchingEmailIndex]] });
        }
      }
      return acc;
    }, []);
  };

  const uniqueContactsAndRecentParties = computed(() => {
    const res: Record<string, IRecipientInputItemNonGroup> = {};

    // Filter contacts based on user input
    const filteredContacts = filterContactsByInput(contactsData.value || [], userInputString.value);

    // Map filtered contacts to IRecipientInputItemContact
    const mappedContacts = filteredContacts.map<IRecipientInputItemContact>((curr) => {
      const address = curr.emails?.[0] ?? '';
      return {
        type: ERecipientInputItemTypes.CONTACT,
        id: curr.id,
        name: curr.fullName ?? '',
        address,
        valid: validateEmail(address),
        hasAvatar: curr.hasAvatar,
        props: { disabled: selectedEmailsMap?.value[curr.emails?.[0] ?? ''] ?? false }
      };
    });

    mappedContacts.forEach((item) => {
      res[item.address] = item;
    });

    // Add recent parties to the contacts map
    recentPartiesData.value?.forEach((curr) => {
      if (!res[curr.address]) {
        const item: IRecipientInputItemRecentParty = {
          type: ERecipientInputItemTypes.RECENT_PARTY,
          id: curr.id,
          name: curr.name || '',
          address: curr.address,
          valid: validateEmail(curr.address),
          hasAvatar: false,
          props: { disabled: selectedEmailsMap?.value[curr.address] ?? false }
        };
        res[item.address] = item;
      }
    });

    // Sort by the disabled status to put selected emails at the bottom
    return Object.values(res).sort((a, b) => {
      if (a.props?.disabled && !b.props?.disabled) return 1;
      if (!a.props?.disabled && b.props?.disabled) return -1;
      return 0;
    });
  });

  const uniqueContactsAndRecentPartiesAndGroups = computed<IRecipientInputItem[]>(() => {
    const selectedEmails = new Set(Object.keys(selectedEmailsMap?.value ?? {}));

    const mappedContacts = uniqueContactsAndRecentParties.value.map<IRecipientInputItemNonGroup>(
      (contact) => ({
        ...contact,
        props: {
          disabled: selectedEmails.has(contact.address) // Ensure disabled is always a boolean
        }
      })
    );

    const mappedGroups = contactGroupsWithContacts.value.map<IRecipientInputItemGroup>((group) => {
      const allGroupContactsSelected = group.contacts.every((email) => selectedEmails.has(email));

      return {
        ...group,
        props: {
          disabled: allGroupContactsSelected // Disable group if all contacts are selected
        }
      };
    });

    // Combine and cast to the expected type
    return [...mappedGroups, ...mappedContacts];
  });

  const contactCountCache = computed(() => {
    if (!selectedEmailsMap) {
      return {};
    }
    return (
      // Depending on which version the client wants, comment one of these lines in/out
      // This one will show the domain if in the selection there are multiple addresses with the same name
      Object.keys(selectedEmailsMap.value).reduce<Record<string, Record<string, number>>>(
        (res, entry) => {
          // This one will show domains if there are multiple addresses with the same name selected
          //Object.keys(uniqueContactsAndRecentParties.value).reduce<Record<string, Record<string, number>>>((res, address) => {

          // Find the contact based on the email address
          const contact = uniqueContactsAndRecentParties.value.find(
            (contact) => contact.address === entry
          );

          if (!contact || typeof contact !== 'object') {
            return res;
          }

          const domain = entry.split('@')[1];
          const name = contact.name;

          if (!name) {
            return res;
          }

          if (!domain) {
            return res;
          }

          if (!res[name]) {
            res[name] = {};
          }

          if (!res[name][domain]) {
            res[name][domain] = 1;
          } else {
            res[name][domain] += 1;
          }

          return res;
        },
        {}
      ) ?? {}
    );
  });

  const getContactChipName = (address: string, name?: string) => {
    const domain = address.split('@')[1];
    if (!name) {
      return `${address}`;
      // TODO: Re enable and probably rework the logic for targets in settings
      // } else if (
      //   contactCountCache.value[name] &&
      //   Object.keys(contactCountCache.value[name]).length === 1
      // ) {
      //   return `${name}`;
    } else if (
      domain &&
      contactCountCache.value[name] &&
      contactCountCache.value[name][domain] &&
      contactCountCache.value[name][domain] > 1
    ) {
      return `${name} (${address})`;
    }
    return `${name} (${domain})`;
  };

  const getContactByEmail = async (
    email: string,
    includeRecentParties = true
  ): Promise<IRecipientInputItemNonGroup> => {
    // Check the cache first
    const cachedContact = uniqueContactsAndRecentParties.value.find(
      (contact) => contact.address === email
    );

    if (cachedContact) {
      return cachedContact;
    }

    // Check contacts list
    const contactFromList = contactsData.value?.find((contact) => contact.emails?.includes(email));

    if (contactFromList && contactFromList.emails?.[0] !== undefined) {
      return {
        type: ERecipientInputItemTypes.CONTACT,
        id: contactFromList.id,
        name: contactFromList.fullName ?? '',
        address: contactFromList.emails[0],
        valid: true
      };
    }

    if (includeRecentParties) {
      const recentPartiesQuery = await queryClient.ensureQueryData(
        getRecentPartiesQueryOptions(email)
      );

      const contactFromRecent = recentPartiesQuery?.find(
        (party: IRecentParty) => party.address === email
      );

      if (contactFromRecent) {
        return {
          type: ERecipientInputItemTypes.RECENT_PARTY,
          id: contactFromRecent.id,
          name: contactFromRecent.name || '',
          address: contactFromRecent.address,
          valid: true,
          hasAvatar: false
        };
      }
    }

    // If not found, return the email without a name
    return {
      type: ERecipientInputItemTypes.CUSTOM,
      address: email,
      name: '',
      valid: true,
      hasAvatar: false
    };
  };

  const getFilteredContactsAndRecipients = computed<IRecipientInputItemNonCustom[]>(() => {
    const input = userInputString.value.toLowerCase().trim();

    // Track emails to avoid duplicate results across sources
    const matchedEmails = new Set<string>();

    // Filter contacts based on the input
    const filteredContacts =
      contactsData.value?.filter((contact) => {
        const matches =
          contact.fullName?.toLowerCase().includes(input) ||
          contact.emails?.some((email) => email.toLowerCase().includes(input));
        if (matches) {
          // Track matched emails to avoid duplicates with recent parties
          contact.emails.forEach((email) => matchedEmails.add(email));
        }
        return matches;
      }) || [];

    // Map filtered contacts to the expected structure
    const mappedContacts = filteredContacts.map<IRecipientInputItemContact>((contact) => ({
      id: contact.id,
      type: ERecipientInputItemTypes.CONTACT,
      name: contact.fullName ?? '',
      address: contact.emails?.[0] ?? '',
      valid: true,
      hasAvatar: contact.hasAvatar,
      props: { disabled: selectedEmailsMap?.value[contact.emails?.[0] ?? ''] ?? false }
    }));

    const mappedRecentParties =
      recentPartiesData.value
        ?.filter(
          (party) =>
            ((party.name || '').toLowerCase().includes(input) ||
              party.address.toLowerCase().includes(input)) &&
            !matchedEmails.has(party.address)
        )
        .map<IRecipientInputItemRecentParty>((party) => ({
          id: party.id,
          type: ERecipientInputItemTypes.RECENT_PARTY,
          name: party.name || '',
          address: party.address,
          valid: true,
          hasAvatar: false,
          props: { disabled: selectedEmailsMap?.value[party.address] ?? false }
        })) || [];

    return [...mappedContacts, ...mappedRecentParties].sort((a, b) => {
      if (a.props?.disabled && !b.props?.disabled) return 1;
      if (!a.props?.disabled && b.props?.disabled) return -1;
      return 0;
    });
  });

  return {
    userInputString,
    debouncedUserInputString,
    contactsData,
    recentPartiesData,
    uniqueContactsAndRecentPartiesAndGroups,
    contactCountCache,
    getFilteredContactsAndRecipients,
    isContactsListFetching,
    isRecentPartiesFetching,
    getContactChipName,
    getContactByEmail
  };
}
